import { Injectable, Inject } from '@angular/core';
import type { Socket } from 'socket.io-client';
import { ConfigService } from '../application/config.service.';
import { EventService } from '../event/event.service';
import { APP_BASE_HREF } from '@angular/common';
import { IAppConfig } from 'src/app/interfaces/response.interfaces';
import { LoadService } from '../application/load.service';

const SOCKET_ROUTES = {
    UPDATE_APP_CONFIG: 'update:app-config',
    UNLOCK_TALON: 'unlock:talon',
} as const;

@Injectable({
    providedIn: 'root',
})
export class SocketService {
    private socket?: Socket;
    private socketPromise?: Promise<Socket>;
    private shouldConnect = false;
    public baseURL: string;
    private path: string;
    private isExplicitUrl: boolean;

    public constructor(
        private configS: ConfigService,
        private loadService: LoadService,
        private eventService: EventService,
        @Inject(APP_BASE_HREF) private baseHref: string,
    ) {
        const hostBackend = configS.getValue('hostBackend') as string;
        this.isExplicitUrl = hostBackend.match(/http(s?):\/\//) ? true : false;
        this.baseURL = this.isExplicitUrl ? hostBackend : window.location.host;
        this.path = this.isExplicitUrl ? `api/v2/socket` : `api/api/v2/socket`;

        this._addDefaultListeners();
    }

    public connect() {
        this.shouldConnect = true;
        void this._getSocket()
            .then((socket) => {
                // `active` также учитывает уже начавшееся подключение, поэтому
                // параллельные вызовы connect() не запускают несколько попыток.
                if (this.shouldConnect && !socket.active) socket.connect();
            })
            .catch((error) =>
                console.error('Не удалось загрузить Socket.IO:', error),
            );
    }

    public disconnect() {
        this.shouldConnect = false;
        this.socket?.disconnect();
    }

    private _setupListeners(socket: Socket) {
        socket.on('connect', () => console.log('✅ Socket connected'));
        socket.on('disconnect', () => console.log('❌ Socket disconnected'));

        // слушатель конфига
        socket.on(SOCKET_ROUTES.UPDATE_APP_CONFIG, (newConfig: IAppConfig) => {
            // Обновляем конфиг
            this.loadService.updateAppConfig(newConfig);
        });
    }

    public unlockTalon(rnumbID: number) {
        void this._getSocket()
            .then((socket) => socket.emit(SOCKET_ROUTES.UNLOCK_TALON, rnumbID))
            .catch((error) =>
                console.error('Не удалось загрузить Socket.IO:', error),
            );
    }

    private _getSocket(): Promise<Socket> {
        if (this.socket) return Promise.resolve(this.socket);
        if (this.socketPromise) return this.socketPromise;

        this.socketPromise = import('socket.io-client')
            .then(({ io }) => {
                const socket = io(this.baseURL, {
                    path: this.baseHref + this.path,
                    transports: ['pooling', 'websocket'],
                    upgrade: true,
                    withCredentials: true,
                    autoConnect: false,

                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    timeout: 20_000,
                });

                this._setupListeners(socket);
                this.socket = socket;

                return socket;
            })
            .catch((error) => {
                this.socketPromise = undefined;
                throw error;
            });

        return this.socketPromise;
    }

    private _addDefaultListeners() {
        this.eventService.on('auth:login', () => this.connect());
        this.eventService.on('auth:login-success', () => {
            this.disconnect(); // при успешной авторизации сначала отключаемся
            this.connect(); // затем заново конектимся
        });
        this.eventService.on('auth:logout', () => {
            this.disconnect();
            this.connect();
        });
    }
}
